import type { z } from "zod";

/** API のエラー。message は利用者に見せられる日本語（docs/api-contract.md） */
export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiFailure";
  }
  get isNetwork() {
    return this.status === 0;
  }
}

export const MSG_NETWORK = "通信できませんでした。電波の状態を確認して、もう一度お試しください。";
export const MSG_TIMEOUT = "応答がありませんでした。しばらくしてから、もう一度お試しください。";
export const MSG_SERVER = "サーバーで問題が起きました。しばらくしてから、もう一度お試しください。";
export const MSG_FORBIDDEN = "この操作の権限がありません。";
/** AI の 1 日の上限（429 ai_daily_limit。デモの利用者は 1 日 100 回・300 円） */
export const MSG_AI_DAILY_LIMIT = "本日の AI の利用上限に達しました。明日またお試しください。";
export const MSG_UNAUTHORIZED = "接続が切れました。もう一度お試しください。";

type Options = {
  body?: unknown;
  form?: FormData;
  /** 読み込みは 8 秒で打ち切る（R11）。書き込み・アップロードは長め */
  timeoutMs?: number;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
};

let onUnauthorized: ((code: string) => void) | null = null;
/** 401 を受けたとき（ログアウトさせる）。AuthProvider が登録する */
export function setUnauthorizedHandler(fn: ((code: string) => void) | null) {
  onUnauthorized = fn;
}

export async function apiRequest<T = unknown>(method: string, path: string, opts: Options = {}, schema?: z.ZodType<T>): Promise<T> {
  const timeoutMs = opts.timeoutMs ?? (method === "GET" ? 8_000 : 30_000);
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const abortFromOuter = () => ctrl.abort();
  opts.signal?.addEventListener("abort", abortFromOuter);
  const headers: Record<string, string> = { Accept: "application/json" };
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await (opts.fetchImpl ?? fetch)(path, { method, headers, body, credentials: "same-origin", signal: ctrl.signal });
  } catch (e) {
    if (opts.signal?.aborted) throw e;
    if (ctrl.signal.aborted) throw new ApiFailure(0, "timeout", MSG_TIMEOUT);
    throw new ApiFailure(0, "network", MSG_NETWORK);
  } finally {
    clearTimeout(timer);
    opts.signal?.removeEventListener("abort", abortFromOuter);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = undefined;
    }
  }
  if (!res.ok) {
    const err = errorFrom(res.status, json);
    // 入口（/api/auth/demo）自体の 401 では入り直さない（繰り返さないため）
    if (res.status === 401 && !path.startsWith("/api/auth/demo")) onUnauthorized?.(err.code);
    throw err;
  }
  if (schema) {
    const parsed = schema.safeParse(json);
    if (!parsed.success) throw new ApiFailure(res.status, "bad_response", "受け取ったデータの形が正しくありません。画面を読み込み直してください。");
    return parsed.data;
  }
  return json as T;
}

export function errorFrom(status: number, json: unknown): ApiFailure {
  const e = (json as { error?: { code?: unknown; message?: unknown } } | undefined)?.error;
  const code = typeof e?.code === "string" ? e.code : `http_${status}`;
  if (code === "ai_daily_limit") return new ApiFailure(status, code, MSG_AI_DAILY_LIMIT);
  if (typeof e?.message === "string" && e.message.trim()) return new ApiFailure(status, code, e.message);
  if (status === 401) return new ApiFailure(status, code, MSG_UNAUTHORIZED);
  if (status === 403) return new ApiFailure(status, code, MSG_FORBIDDEN);
  if (status === 404) return new ApiFailure(status, code, "見つかりませんでした。削除された可能性があります。");
  if (status === 413) return new ApiFailure(status, code, "ファイルが大きすぎます。100MB までのファイルを選んでください。");
  if (status === 502 || status === 503 || status === 504) return new ApiFailure(status, code, "サーバーにつながりませんでした。しばらくしてから、もう一度お試しください。");
  if (status === 429) return new ApiFailure(status, code, "操作が続いたため、少し待ってからお試しください。");
  return new ApiFailure(status, code, MSG_SERVER);
}

export function messageOf(e: unknown): string {
  if (e instanceof ApiFailure) return e.message;
  return "処理できませんでした。もう一度お試しください。";
}

export const api = {
  get: <T>(path: string, schema?: z.ZodType<T>, opts?: Options) => apiRequest<T>("GET", path, opts, schema),
  post: <T>(path: string, body?: unknown, schema?: z.ZodType<T>, opts?: Options) => apiRequest<T>("POST", path, { ...opts, body }, schema),
  put: <T>(path: string, body?: unknown, schema?: z.ZodType<T>, opts?: Options) => apiRequest<T>("PUT", path, { ...opts, body }, schema),
  patch: <T>(path: string, body?: unknown, schema?: z.ZodType<T>, opts?: Options) => apiRequest<T>("PATCH", path, { ...opts, body }, schema),
  del: <T>(path: string, schema?: z.ZodType<T>, opts?: Options) => apiRequest<T>("DELETE", path, opts, schema),
  upload: (file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return apiRequest<{ id: string; key: string; name: string; size: number; contentType: string }>("POST", "/api/files", { form, timeoutMs: 180_000 });
  },
};

/** クエリ文字列（空の値は入れない） */
export function qs(params: Record<string, string | number | null | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== null && v !== undefined && v !== "") u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : "";
}
